import { useMemo, useState } from 'react';
import { Email, OpenInNew } from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { USER_ROLE } from '@/config/constants';
import {
  useGetAdminUserPipelineQuery,
  useUpdateUserByAdminMutation,
} from '@/apis/extended/admin/userApi';
import { AdminMessageDialog } from '@/components/Admin/AdminWorkspace/AdminMessageDialog';
import { AdminSearchResultsPanel } from '@/components/Admin/AdminWorkspace/AdminSearchResultsPanel';
import {
  AttestationChip,
  daysAgoLabel,
  displayNameOf,
  DocumentsSummary,
  humanizeKey,
} from '@/components/Admin/AdminWorkspace/loanHealth';
import { SortableTableCell } from '@/components/Common/SortableTableCell';
import { useTableSort } from '@/hooks/useTableSort';
import type { AdminPipelineUser } from '@/types/UserType';

/**
 * Real pipeline stages, in funnel order — mirrors services/adminPipeline.service.js
 * on the backend, which computes each user's `pipelineStage` from live data.
 * The first four follow the backlog funnel: Lead → Engaged → Budget → Approved.
 */
const pipelineSteps = [
  { label: 'Lead', key: 'lead' },
  { label: 'Engaged', key: 'engaged_shop' },
  { label: 'Budget', key: 'qualify' },
  { label: 'Application', key: 'application' },
  { label: 'Documents', key: 'documents' },
  { label: 'Approved', key: 'pre_approved' },
  { label: 'Tours', key: 'tours' },
  { label: 'Offer Submission', key: 'offer_submission' },
  { label: 'Negotiate', key: 'negotiate' },
  { label: 'Processing', key: 'processing' },
  { label: 'Conditional Approval', key: 'conditional_approval' },
  { label: 'Underwriting Approval', key: 'approval' },
  { label: 'Closing', key: 'closing' },
  { label: 'Closed', key: 'closed' },
];
const stageLabel = (key?: string) =>
  pipelineSteps.find((s) => s.key === key)?.label ?? humanizeKey(key);

const ROLE_FILTERS = [
  { key: 'people', label: 'Everyone but admins' },
  { key: 'buyers', label: 'Buyers' },
  { key: 'sellers', label: 'Sellers' },
  { key: 'agents', label: 'Agents' },
  { key: 'admins', label: 'Admins' },
] as const;
type RoleFilter = (typeof ROLE_FILTERS)[number]['key'];

function matchesRole(role: string, filter: RoleFilter) {
  switch (filter) {
    case 'buyers':
      return role === USER_ROLE.USER || role === USER_ROLE.BUYER;
    case 'sellers':
      return role === USER_ROLE.SELLER;
    case 'agents':
      return role === USER_ROLE.SELLER_AGENT;
    case 'admins':
      return role === USER_ROLE.ADMIN;
    default:
      return role !== USER_ROLE.ADMIN;
  }
}

const ROLE_LABELS: Record<string, string> = {
  user: 'Buyer',
  buyer: 'Buyer',
  seller: 'Seller',
  'seller-agent': 'Agent',
  admin: 'Admin',
};

const ALL = 'all';
const MANUAL = 'manual';

export const AdminUser = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const searchTerm = params.get('q') ?? '';
  const stage = params.get('stage') ?? ALL;
  const roleFilter = (params.get('role') as RoleFilter) || 'people';
  const setParam = (key: string, value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const [messageUser, setMessageUser] = useState<AdminPipelineUser | null>(
    null
  );

  const { data: usersData, isLoading, error } = useGetAdminUserPipelineQuery();
  const [updateUserByAdmin] = useUpdateUserByAdminMutation();

  // Search and role first, across every stage; the stage tabs then narrow it
  // and show how many matches each stage holds.
  const matching = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return (usersData?.users ?? []).filter((u) => {
      if (!matchesRole(u.role, roleFilter)) return false;
      if (!q) return true;
      return [u.name, u.email, u.phone, u.username, displayNameOf(u)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [usersData, searchTerm, roleFilter]);

  const stageCounts = useMemo(() => {
    const m: Record<string, number> = {};
    matching.forEach(
      (u) => (m[u.pipelineStage] = (m[u.pipelineStage] || 0) + 1)
    );
    return m;
  }, [matching]);

  const inStage = useMemo(
    () =>
      stage === ALL || stage === MANUAL
        ? matching
        : matching.filter((u) => u.pipelineStage === stage),
    [matching, stage]
  );

  const userSort = useTableSort<AdminPipelineUser>(inStage, {
    name: (u) => displayNameOf(u).toLowerCase(),
    email: (u) => u.email,
    stage: (u) => pipelineSteps.findIndex((s) => s.key === u.pipelineStage),
    approvalAmount: (u) => u.approvalAmount ?? null,
    productCount: (u) => u.productCount || 0,
    activity: (u) => u.leadApplication?.insights.daysSinceActivity ?? null,
    manualStage: (u) =>
      pipelineSteps.findIndex((step) => step.key === u.manualPipelineStage) +
        1 || null,
  });
  const rows = userSort.sortedRows;

  const headSx = {
    bgcolor: alpha(theme.palette.primary.main, 0.07),
    '& .MuiTableCell-head': {
      color: theme.palette.text.primary,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
  };
  // Actions stay on screen while the table scrolls sideways.
  const stickyRightSx = {
    position: 'sticky' as const,
    right: 0,
    bgcolor: theme.palette.background.paper,
    boxShadow: `-6px 0 8px -6px ${alpha(theme.palette.common.black, 0.2)}`,
    zIndex: 1,
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Loading users...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load users. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
        Users
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search name, email or phone — all stages"
          value={searchTerm}
          onChange={(e) => setParam('q', e.target.value, '')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i className="fa-light fa-search" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: { sm: 460 } }}
        />
        <TextField
          select
          size="small"
          label="Who"
          value={roleFilter}
          onChange={(e) => setParam('role', e.target.value, 'people')}
          sx={{ minWidth: 200 }}
        >
          {ROLE_FILTERS.map((r) => (
            <MenuItem key={r.key} value={r.key}>
              {r.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {searchTerm.trim().length >= 2 && (
        <AdminSearchResultsPanel query={searchTerm} hideUsers />
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={stage}
          onChange={(_e, v) => setParam('stage', v, ALL)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ '& .MuiTab-root': { whiteSpace: 'nowrap' } }}
        >
          <Tab
            value={ALL}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                All stages
                <Chip label={matching.length} size="small" color="primary" />
              </Box>
            }
          />
          {pipelineSteps.map((step) => (
            <Tab
              key={step.key}
              value={step.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {step.label}
                  <Chip
                    label={stageCounts[step.key] || 0}
                    size="small"
                    color={stageCounts[step.key] ? 'primary' : 'default'}
                  />
                </Box>
              }
            />
          ))}
          <Tab value={MANUAL} label="Manual stage" />
        </Tabs>
      </Box>

      {stage === MANUAL ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <SortableTableCell sortKey="name" sort={userSort}>
                  Person
                </SortableTableCell>
                <SortableTableCell sortKey="email" sort={userSort}>
                  Email
                </SortableTableCell>
                <SortableTableCell sortKey="manualStage" sort={userSort}>
                  Manual stage
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{displayNameOf(user)}</TableCell>
                  <TableCell>{user.email || '—'}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={user.manualPipelineStage || ''}
                      onChange={(e) =>
                        updateUserByAdmin({
                          id: user._id,
                          manualPipelineStage: String(e.target.value) || null,
                        })
                      }
                      displayEmpty
                      sx={{ minWidth: 220 }}
                    >
                      <MenuItem value="">Automatic</MenuItem>
                      {pipelineSteps.map((step) => (
                        <MenuItem key={step.key} value={step.key}>
                          {step.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ mt: 2, overflowX: 'auto', maxWidth: '100%' }}
        >
          <Table size="small" sx={{ minWidth: 1150 }}>
            <TableHead>
              <TableRow sx={headSx}>
                <SortableTableCell sortKey="name" sort={userSort}>
                  Person
                </SortableTableCell>
                <SortableTableCell sortKey="email" sort={userSort}>
                  Contact
                </SortableTableCell>
                <SortableTableCell sortKey="stage" sort={userSort}>
                  Stage
                </SortableTableCell>
                <TableCell>Loan file</TableCell>
                <TableCell>Documents</TableCell>
                <SortableTableCell sortKey="activity" sort={userSort}>
                  Last activity
                </SortableTableCell>
                <SortableTableCell sortKey="approvalAmount" sort={userSort}>
                  Approval amount
                </SortableTableCell>
                <SortableTableCell sortKey="productCount" sort={userSort}>
                  Properties
                </SortableTableCell>
                <TableCell sx={stickyRightSx}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((user) => {
                const lead = user.leadApplication;
                return (
                  <TableRow
                    key={user._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/user/${user._id}`)}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                      >
                        <Avatar sx={{ width: 34, height: 34, fontSize: 15 }}>
                          {displayNameOf(user).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {displayNameOf(user)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ROLE_LABELS[user.role] ?? humanizeKey(user.role)}
                            {user.flowType === 'refinance'
                              ? ' · Refinance'
                              : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.email || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.phone || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {stageLabel(user.pipelineStage)}
                      {user.manualPipelineStage ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Set manually
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {lead ? (
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Typography variant="body2">
                            {humanizeKey(lead.insights.lifecycle)}
                          </Typography>
                          <AttestationChip state={lead.insights.attestation} />
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not started
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {lead ? (
                        <DocumentsSummary insights={lead.insights} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {lead
                        ? daysAgoLabel(lead.insights.daysSinceActivity)
                        : user.lastLoginAt
                        ? `Signed in ${new Date(
                            user.lastLoginAt
                          ).toLocaleDateString()}`
                        : '—'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {/* Saves on blur / Enter — each save emails the admin
                          team. Keyed on the saved value so a refetch resets it. */}
                      <TextField
                        key={`${user._id}-${user.approvalAmount ?? ''}`}
                        type="number"
                        placeholder="Not set"
                        defaultValue={user.approvalAmount ?? ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter')
                            (e.target as HTMLInputElement).blur();
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value.trim();
                          const next = raw === '' ? null : Number(raw);
                          if (next !== null && !Number.isFinite(next)) return;
                          if (next === (user.approvalAmount ?? null)) return;
                          updateUserByAdmin({
                            id: user._id,
                            approvalAmount: next,
                          });
                        }}
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        sx={{ width: 150 }}
                      />
                    </TableCell>
                    <TableCell>{user.productCount || 0}</TableCell>
                    <TableCell
                      sx={{ ...stickyRightSx, whiteSpace: 'nowrap' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Open profile">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/admin/user/${user._id}`)}
                        >
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={user.email ? 'Message' : 'No email on file'}
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!user.email}
                            onClick={() => setMessageUser(user)}
                          >
                            <Email fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Alert severity="info">
                      {searchTerm
                        ? `No one matches "${searchTerm}"${
                            stage === ALL ? '' : ` in ${stageLabel(stage)}`
                          }.`
                        : 'No one in this stage.'}
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {messageUser && (
        <AdminMessageDialog
          open
          onClose={() => setMessageUser(null)}
          userId={messageUser._id}
          firstName={displayNameOf(messageUser).split(' ')[0]}
          email={messageUser.email}
          loanFiles={
            messageUser.leadApplication
              ? [
                  {
                    _id: messageUser.leadApplication._id,
                    flowProfile: messageUser.leadApplication.flowProfile,
                    insights: messageUser.leadApplication.insights,
                  },
                ]
              : []
          }
        />
      )}
    </Box>
  );
};
